import path from "path"
import { useNotify, useRefresh, useDataProvider } from "react-admin"
import moment from "moment"
import {
  Button,
  Card,
  CardContent,
  Divider,
  IconButton,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Grid,
} from "@material-ui/core"
import SendIcon from "@material-ui/icons/Send"
import DeleteIcon from "@material-ui/icons/Delete"
import { makeStyles } from "@material-ui/core/styles"
import { red } from "@material-ui/core/colors"

import { storage } from "../../firebase"
import useFiles from "../../hooks/useFiles"
import UploadFileInput from "../../lib/components/UploadFileInput"
import getHttpsCallable from "../../utils/getHttpsCallable"
import React from "react"
import { MoveToInbox } from "@material-ui/icons"
import { MoveFileButton } from "../MoveFileButton"


const uploadFile = getHttpsCallable("admin-bmw-uploadFile")

function subdirToTargetFileName (
  subdir: string,
  filename: string,
): string | undefined {
  const splittedName = filename.split(".")
  const ext = splittedName[splittedName.length - 1]

  const extname = ![
    "salesContracts", "purchaseContracts",
    "vehicleRegistrationDocument", "latestMileage",
  ].includes(subdir) ? ext : filename

  const mapping: Record<string, string> = {
    salesContracts: `Verkaufsvertrag_${extname}`,
    salesContractsMerged: `Verkaufsvertrag.${extname}`,
    purchaseContracts: `Kaufvertrag_${extname}`,
    purchaseContractsMerged: `Kaufvertrag.${extname}`,
    vehicleRegistrationDocument: `Fahrzeugschein_${extname}`,
    vehicleRegistrationDocumentMerged: `Fahrzeugschein.${extname}`,
    latestMileage: `Nachweis-Aktueller-KM-Stand_${extname}`,
    latestMileageMerged: `Nachweis-Aktueller-KM-Stand.${extname}`,
    claimPoaSignature: `Unterschrift-Vollmacht.${extname}`,
    claimFinancingAgreementSignature:
      `Unterschrift-Finanzierungsvertrag.${extname}`,
  }

  return mapping[subdir] ?? filename
}


const storageRef = storage.ref()

const useStyles = makeStyles(theme => ({
  deleteIcon: {
    color: red[500],
  },
}))


interface CheckUploadedFileParams {
  fileName: string
}


function FileTable (
  { claimId, files, lawFirm, lawFirmCaseId, subdir, claimType}: {
    claimId: string
    files: ClaimFile[]
    lawFirm: string
    lawFirmCaseId?: string
    subdir: string
    claimType: string
  },
) {
  const classes = useStyles()
  const notify = useNotify()
  const refresh = useRefresh()
  const formatString = "YYYY-MM-DD\u{00A0}\u{00A0}HH:mm:ss\u{00A0}\u{00A0}"

  async function confirmDelete (path: string) {
    if (window.confirm("Are you sure you want to delete this file?")) {
      try {
        await storageRef.child(path).delete()
        notify("File was successfully deleted", "success")
        // TODO: Also delete document from Firestore
        refresh()
      }
      catch (error: any) {
        notify(error?.message, "warning")
      }
    }
  }

  if (!files || files.length === 0) {
    return <br/>
  }

  return (
    <Paper>
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Name</TableCell>
            <TableCell>Created</TableCell>
            <TableCell>Updated</TableCell>
            <TableCell>Transmit to Law Firm</TableCell>
            <TableCell>Move</TableCell>
            <TableCell>Delete</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {files.map(file => {
            return (
              <TableRow key={file.downloadURL}>
                <TableCell component="th" scope="row">
                  <Link
                    target="_blank"
                    rel="noopener noreferrer"
                    href={file.downloadURL}
                  > {file.name}</Link>
                </TableCell>
                <TableCell>
                  {moment(file.meta?.timeCreated).format(formatString)}Z
                </TableCell>
                <TableCell>
                  {moment(file.meta?.updated).format(formatString)}Z
                </TableCell>
                <TableCell>
                  {lawFirmCaseId && subdir !== "idCard" &&
                    <Button
                      variant="outlined"
                      onClick={async () => {
                        console.info(
                          `Transmit file "${file.name}" to ${lawFirm}`,
                        )
                        try {
                          await uploadFile({
                            filePath: file.meta?.fullPath,
                            targetFileName: subdirToTargetFileName(
                              subdir,
                              path.basename(file.meta?.fullPath),
                            ),
                            lawFirm,
                            internalId: lawFirmCaseId,
                            claimId,
                            shouldUpdateCase: false,
                          })

                          notify(
                            `Transmitted file "${file.meta.name}" 
                            to ${lawFirm}`,
                            "success",
                          )
                        }
                        catch (error: any) {
                          notify(error?.message, "warning")
                        }
                      }}
                    >
                      <SendIcon/>
                      &nbsp;
                      <small>Transmit to {lawFirm}</small>
                    </Button>
                  }
                  {!lawFirmCaseId &&
                    <small>
                      Claim must be transmitted<br/>
                      to law firm first
                    </small>
                  }
                </TableCell>
                <TableCell>
                  <MoveFileButton
                    filePath={file.meta?.fullPath}
                    claimType={claimType}
                    claimId={claimId}
                  />
                </TableCell>
                <TableCell>
                  <IconButton
                    onClick={event => {
                      event.preventDefault()
                      confirmDelete(file.meta?.fullPath)
                    }}
                  >
                    <DeleteIcon className={classes.deleteIcon} />
                  </IconButton>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
    </Paper>
  )
}


export default function BMWClaimFiles (
  props: {
    record?: {
      userId: string
      id: string
      lawFirm: string
      lawFirmCaseId: string
      customFieldValues: any
      type: string
    },
  },
) {
  const userId = props?.record?.userId
  const customFieldValues = props?.record?.customFieldValues
  const notify = useNotify()

  if (!userId) {
    return <Card>
      <CardContent>userId must be specified</CardContent>
    </Card>
  }

  const claimId = props?.record?.id
  if (!claimId) {
    return <Card>
      <CardContent>claimId must be specified</CardContent>
    </Card>
  }

  const lawFirm = props?.record?.lawFirm

  const lawFirmCaseId = props?.record?.lawFirmCaseId

  const [files, loading] = useFiles({ userId, claimId })

  if (loading) {
    return <>
      <p>Loading ...</p>
      <p>(Please hard reload the website if this seems stuck)</p>
    </>
  }

  const uploadSections = [
    {
      title: "Purchase Contract Files (KV)",
      files: files.purchaseContracts,
      subdir: "purchaseContracts",
      prefix: "Kaufvertrag_",
      mergedFile: files.purchaseContractMerged,
      targetDir: "purchaseContractMerged",
      leoFieldId: 425,
    },
    {
      title: "Sales Contract Files",
      files: files.salesContracts,
      subdir: "salesContracts",
      prefix: "Verkaufsvertrag_",
      mergedFile: files.salesContractMerged,
      targetDir: "salesContractMerged",
      leoFieldId: 490,
    },
    {
      title: "Vehicle Registration Document",
      files: files.vehicleRegistrationDocument,
      subdir: "vehicleRegistrationDocument",
      prefix: "Fahrzeugschein_",
      mergedFile: files.vehicleRegistrationDocumentMerged,
      targetDir: "vehicleRegistrationDocumentMerged",
      leoFieldId: 424,
    },
    {
      title: "Latest Mileage (Photo)",
      files: files.latestMileage,
      subdir: "latestMileage",
      prefix: "Nachweis-Aktueller-KM-Stand_",
      mergedFile: files.latestMileageMerged,
      targetDir: "latestMileageMerged",
      leoFieldId: 519,
    },
    {
      title: "Power of Attorney Signature",
      files: files.claimPoaSignature,
      subdir: "claimPoaSignature",
      prefix: "Unterschrift-Vollmacht_",
      leoFieldId: 1308,
    },
    {
      title: "LFA Signature",
      files: files.claimFinancingAgreementSignature,
      subdir: "claimFinancingAgreementSignature",
      prefix: "Unterschrift-Finanzierungsvertrag_",
      leoFieldId: undefined,
    },
    {
      title: "Other Files",
      files: files.otherFiles,
      subdir: "otherFiles",
    },
  ]

  return (
    <div style={{ padding: 20 }}>
      {
        uploadSections.map(section =>
          <div key={section.title}>
            <Typography variant="overline">
              {section.title}
            </Typography>
            <FileTable
              claimId={claimId}
              lawFirmCaseId={lawFirmCaseId}
              files={section.files}
              lawFirm={lawFirm || ""}
              subdir={section.subdir}
              claimType={props.record?.type ?? ""}
            />
            <br/>

            <Grid container>
              <UploadFileInput
                buttonLabel="Upload new file"
                subdir={section.subdir}
                initialUploadedFiles={[]}
                claimId={claimId}
                userId={userId}
                customFieldValues={customFieldValues}
                onChange={event => {
                  console.info(event)
                }}
              />
            </Grid>

            <Divider style={{ margin: "20px 0" }} />
          </div>,
        )
      }
    </div>
  )
}
